import React from "react";
import { Link } from "react-router-dom";

/**
 * ServicesMegaMenu — refined editorial index panel.
 * Institutional structure: left column = statement of intent,
 * right column = two chapters (Consulting · Training) as minimal editorial
 * cards. Deliberately restrained, no CTAs on the cards themselves —
 * the whole card is the click surface.
 */
export default function ServicesMegaMenu({ open, onOpen, onClose }) {
  return (
    <>
      {/* Backdrop — deeper ink veil for gravitas */}
      <div
        aria-hidden
        className="fixed inset-x-0 transition-opacity duration-500"
        style={{
          top: 0,
          height: "100vh",
          background: "rgba(20,20,15,0.55)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          zIndex: 45,
        }}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        data-testid="services-mega-menu"
        onMouseEnter={onOpen}
        onMouseLeave={onClose}
        className="absolute left-0 right-0 overflow-hidden"
        style={{
          top: "100%",
          background: open ? "#F4EFE7" : "transparent",
          borderBottom: open ? "1px solid rgba(20,20,15,0.14)" : "none",
          maxHeight: open ? "1000px" : "0px",
          transition: "max-height 0.7s cubic-bezier(0.19, 1, 0.22, 1)",
          zIndex: 50,
          color: "#14140F",
          pointerEvents: open ? "auto" : "none",
        }}
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 pt-14 md:pt-16 pb-14 md:pb-16">
          {/* Running header removed per user request */}
          <div className="mb-14" style={{ borderBottom: "1px solid rgba(20,20,15,0.14)" }} />

          {/* Asymmetric grid: statement · chapters */}
          <div className="grid grid-cols-12 gap-10 md:gap-14">
            {/* Left — editorial statement of intent */}
            <div className="col-span-12 md:col-span-3 md:col-start-1">
              <p
                className="font-eyebrow"
                style={{
                  color: "#8C6B3F",
                  letterSpacing: "0.32em",
                  fontSize: "0.68rem",
                }}
              >
                The House of Impact
              </p>
              <div
                className="hairline mt-4"
                style={{ width: "24px", background: "#8C6B3F" }}
              />
              <h2
                className="title-caps mt-8 leading-[1.0]"
                style={{
                  fontSize: "clamp(1.4rem, 1.9vw, 1.9rem)",
                  letterSpacing: "-0.01em",
                  color: "#14140F",
                  textWrap: "balance",
                }}
              >
                Two disciplines.
                <br />
                One{" "}
                <span
                  className="font-script"
                  style={{
                    textTransform: "none",
                    letterSpacing: 0,
                    fontSize: "1.25em",
                    color: "#8C6B3F",
                  }}
                >
                  House.
                </span>
              </h2>
              <p
                className="font-serif-italic mt-8 opacity-75"
                style={{
                  fontSize: "0.98rem",
                  lineHeight: 1.6,
                  color: "#14140F",
                  maxWidth: "34ch",
                  fontVariationSettings:
                    '"SOFT" 60, "WONK" 1, "opsz" 72',
                }}
              >
                We work in two disciplines — Consulting and Training. The first defines direction, the second transmits savoir-faire. Together they shape the culture, capability and discipline of an exceptional brand.
              </p>
            </div>

            {/* Right — two editorial chapter cards */}
            <div className="col-span-12 md:col-span-9 md:col-start-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12">
                {/* Chapter I — Consulting */}
                <Link
                  to="/services/consulting"
                  data-testid="mega-consulting-link"
                  className="group block"
                  onClick={onClose}
                >
                  <div className="overflow-hidden bg-[#0A0A08]">
                    <img
                      src="https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/lk14wck3_Screenshot%202026-07-16%20at%2010.02.53%E2%80%AFpm.png"
                      alt="Consulting"
                      className="w-full aspect-[16/10] object-cover transition-all duration-[1200ms] ease-out"
                      style={{
                        filter:
                          "grayscale(1) contrast(1.25) brightness(0.92)",
                      }}
                    />
                  </div>
                  <div
                    className="mt-6 pt-5 flex items-baseline justify-between"
                    style={{ borderTop: "1px solid rgba(20,20,15,0.14)" }}
                  >
                    <p
                      className="font-eyebrow"
                      style={{
                        color: "#8C6B3F",
                        letterSpacing: "0.32em",
                        fontSize: "0.66rem",
                      }}
                    >
                      Chapter I
                    </p>
                    <span
                      className="font-eyebrow opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                      style={{
                        color: "#8C6B3F",
                        letterSpacing: "0.32em",
                        fontSize: "0.66rem",
                      }}
                    >
                      Enter ⟶
                    </span>
                  </div>
                  <h3
                    className="title-caps mt-4 leading-[0.95]"
                    style={{
                      fontSize: "clamp(1.6rem, 2.4vw, 2.1rem)",
                      letterSpacing: "-0.008em",
                    }}
                  >
                    CONSULTING
                  </h3>
                  <p
                    className="font-body mt-4 opacity-75"
                    style={{
                      fontSize: "0.96rem",
                      lineHeight: 1.6,
                      maxWidth: "36ch",
                    }}
                  >
                    Strategy engineered to survive on the floor, not just the boardroom.
                  </p>
                </Link>

                {/* Chapter II — Training */}
                <Link
                  to="/services/training"
                  data-testid="mega-training-link"
                  className="group block"
                  onClick={onClose}
                >
                  <div className="overflow-hidden bg-[#0A0A08]">
                    <img
                      src="https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/z5aw6lut_Screenshot%202026-07-16%20at%2012.32.37%E2%80%AFam.png"
                      alt="Training"
                      className="w-full aspect-[16/10] object-cover transition-all duration-[1200ms] ease-out"
                      style={{
                        filter:
                          "grayscale(1) contrast(1.25) brightness(0.92)",
                      }}
                    />
                  </div>
                  <div
                    className="mt-6 pt-5 flex items-baseline justify-between"
                    style={{ borderTop: "1px solid rgba(20,20,15,0.14)" }}
                  >
                    <p
                      className="font-eyebrow"
                      style={{
                        color: "#8C6B3F",
                        letterSpacing: "0.32em",
                        fontSize: "0.66rem",
                      }}
                    >
                      Chapter II
                    </p>
                    <span
                      className="font-eyebrow opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                      style={{
                        color: "#8C6B3F",
                        letterSpacing: "0.32em",
                        fontSize: "0.66rem",
                      }}
                    >
                      Enter ⟶
                    </span>
                  </div>
                  <h3
                    className="title-caps mt-4 leading-[0.95]"
                    style={{
                      fontSize: "clamp(1.6rem, 2.4vw, 2.1rem)",
                      letterSpacing: "-0.008em",
                    }}
                  >
                    TRAINING
                  </h3>
                  <p
                    className="font-body mt-4 opacity-75"
                    style={{
                      fontSize: "0.96rem",
                      lineHeight: 1.6,
                      maxWidth: "36ch",
                    }}
                  >
                    Culture, capability and human intelligence transmitted to the next reference.
                  </p>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
